import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslocoService } from '@jsverse/transloco';
import { DxTemplateModule, DxTooltipModule } from 'devextreme-angular';
import { DxStepperModule, type DxStepperTypes } from 'devextreme-angular/ui/stepper';
import { getStatusIconClass } from '../status-badge/status-badge.utils';

export interface StatusStepperItem extends DxStepperTypes.Item {
    value: string;
    isActive: boolean;
    iconClass: string | null;
    description: string | null;
    targetId: string;
}

@Component({
    selector: 'app-status-stepper',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [DxStepperModule, DxTemplateModule, DxTooltipModule],
    templateUrl: './status-stepper.component.html',
    styleUrl: './status-stepper.component.scss',
})
export class StatusStepperComponent {
    private readonly translate = inject(TranslocoService);
    private readonly activeLang = toSignal(this.translate.langChanges$, {
        initialValue: this.translate.getActiveLang(),
    });

    /** Full step sequence in display order (e.g. `Object.values(OrderStatus)`). */
    readonly steps = input.required<readonly string[]>();

    /** Currently active step values; multiple values may be active at once. */
    readonly activeSteps = input<readonly string[]>([]);

    /**
     * Builds each label as `{translationPrefix}{step}` when set.
     * Falls back to the raw step value when omitted.
     */
    readonly translationPrefix = input<string | null>(null);

    /**
     * Optional icon class overrides keyed by step value.
     * Falls back to shared status icon map, then to the step number.
     */
    readonly icons = input<Readonly<Record<string, string>> | null>(null);

    /**
     * Optional description overrides keyed by step value.
     * Otherwise uses tooltip keys derived from `translationPrefix` /
     * `descriptionPrefix` (never falls back to the label).
     */
    readonly descriptions = input<Readonly<Record<string, string>> | null>(null);

    /**
     * Builds tooltip keys as `{descriptionPrefix}{step}` when set.
     * When omitted and `translationPrefix` is set, uses:
     * - active: `{translationPrefix}tooltip_{step}`
     * - inactive: `{translationPrefix}tooltip_disabled_{step}`
     *   (falls back to the active tooltip key if the disabled key is missing)
     */
    readonly descriptionPrefix = input<string | null>(null);

    readonly orientation = input<DxStepperTypes.Properties['orientation']>('horizontal');

    /** When true, user selection is blocked (status display). */
    readonly readOnly = input(true);

    /**
     * Block user-driven selection while still allowing controlled `selectedIndex`
     * updates (status changes) through.
     */
    onSelectionChanging(e: DxStepperTypes.SelectionChangingEvent): void {
        if (!this.readOnly()) {
            return;
        }

        const items = this.items();
        const intended = items[this.selectedIndex()];
        const added = e.addedItems?.[0] as StatusStepperItem | undefined;

        if (added && intended && added.value === intended.value) {
            return;
        }

        e.cancel = true;
    }

    readonly items = computed<StatusStepperItem[]>(() => {
        this.activeLang();
        const active = new Set(this.activeSteps());
        const prefix = this.translationPrefix();
        const descriptionPrefix = this.descriptionPrefix();
        const iconOverrides = this.icons();
        const descriptionOverrides = this.descriptions();

        return this.steps().map(value => {
            const isActive = active.has(value);
            const label = prefix ? this.translate.translate(`${prefix}${value}`) : value;
            const iconClass = getStatusIconClass(value, iconOverrides?.[value]) || null;
            const description = descriptionOverrides?.[value] ?? this.resolveDescription(value, isActive, prefix, descriptionPrefix);

            return {
                value,
                label,
                description,
                isActive,
                iconClass,
                targetId: `status-step-${value}`,
            };
        });
    });

    private resolveDescription(
        value: string,
        isActive: boolean,
        translationPrefix: string | null,
        descriptionPrefix: string | null
    ): string | null {
        if (descriptionPrefix) {
            return this.translateIfExists(`${descriptionPrefix}${value}`);
        }

        if (!translationPrefix) {
            return null;
        }

        if (!isActive) {
            const disabled = this.translateIfExists(`${translationPrefix}tooltip_disabled_${value}`);
            if (disabled) {
                return disabled;
            }
        }

        return this.translateIfExists(`${translationPrefix}tooltip_${value}`);
    }

    private translateIfExists(key: string): string | null {
        const translated = this.translate.translate(key);
        return translated && translated !== key ? translated : null;
    }

    /** Furthest active step drives the connector fill; multi-active is applied in onItemRendered. */
    readonly selectedIndex = computed(() => {
        const items = this.items();
        let lastActiveIndex = -1;

        for (let i = 0; i < items.length; i++) {
            if (items[i].isActive) {
                lastActiveIndex = i;
            }
        }

        return Math.max(lastActiveIndex, 0);
    });

    onItemRendered(e: DxStepperTypes.ItemRenderedEvent): void {
        const data = e.itemData as StatusStepperItem | undefined;
        const element = e.itemElement as HTMLElement | undefined;

        if (!data || !element) {
            return;
        }

        element.classList.toggle('dx-step-selected', data.isActive);
        element.classList.toggle('dx-step-completed', false);
        element.classList.toggle('status-step--active', data.isActive);
    }
}
